import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import {
    deleteFileFromSharePoint,
    searchReportTemplateFile,
    uploadFileToSharePoint
} from "../../graph-services/GraphServices";
import {saveAs} from "file-saver";
import {translation} from "../../I18n/i18n";
import {computeTextToMultilevel} from "./MultilevelStructurer";
import {convertDocxFileToPdf} from "../../../src/graph-services/GraphServices"
import {FileExtensionEnum} from "../../Enumerations/FileExtensionEnum";
import {generateTitles} from "./utils";

const moment = require("moment");
const largeSpace = "  ";

async function loadFile(token, webUrl, siteId, language, documentType, callback) {

    let url = await searchReportTemplateFile(token, webUrl, siteId, language)
    PizZipUtils.getBinaryContent(url, callback);
}


function computeLanguageLabel(code) {
    switch (code) {
        case "EN":
            return "English";
        case "FR":
            return "French";
        case "NL":
            return "Dutch";
        case "IT":
            return "Italian";
        case "RU":
            return "Russian";
        case "PL":
            return "Polish";
        case "ES":
            return "Spanish";
        case "PT":
            return "Portuguese";
        case "EL":
            return "Greek";
        case "AR":
            return "Arabic";
        case "DE":
            return "German";
        default:
            return code;

    }
}

function angularParser(tag) {
    let expressions = require('angular-expressions');
    let assign = require("lodash/assign");
    if (tag === '.') {
        return {
            get: function (s) {
                return s;
            }
        };
    }
    const expr = expressions.compile(
        tag.replace(/(’|‘)/g, "'").replace(/(“|”)/g, '"')
    );
    return {
        get: function (scope, context) {
            let obj = {};
            const scopeList = context.scopeList;
            const num = context.num;
            for (let i = 0, len = num + 1; i < len; i++) {
                obj = assign(obj, scopeList[i]);
            }
            return expr(scope, obj);
        }
    };
}

function sortExperiences(experiences) {
    return experiences.length > 0 ? experiences.sort((a, b) => {
        let a_year = a["Period"]["To"]["_attributes"]["year"];
        let a_month = a["Period"]["To"]["_attributes"]["month"].replace("--", "");
        let a_date = a_year === "" ? new Date() : new Date(a_year + "/" + a_month);

        let b_year = b["Period"]["To"]["_attributes"]["year"];
        let b_month = b["Period"]["To"]["_attributes"]["month"].replace("--", "");
        let b_date = b_year === "" ? new Date() : new Date(b_year + "/" + b_month);
        return b_date - a_date;
    }) : experiences;
}

let getLabelByExperience = (e) => {
    return `${e.Employer.Name._text} - ${e.Position.Label._text}`;
}

let translateAndKeepFormat = (formattedDate, language) => {
    if (language && formattedDate.split(largeSpace).length) {
        return `${translation(language, formattedDate.split(largeSpace)[0])}${largeSpace}${formattedDate.split(largeSpace)[1]}`;
    }
    return formattedDate;
}

const computePeriod = (experience, language) => {
    let period = experience.Period;
    let from = period.From;
    let to = period.To;
    let current = period.Current._text
    let formattedFrom = moment((from._attributes.year + "/" + from._attributes.month + "/01").replace("--", "")).format(`MMMM${largeSpace}YYYY`);
    let translatedFrom = translateAndKeepFormat(formattedFrom, language);
    if (current && (!to || to._attributes.year === "")) {
        let translatedTo = translation(language, 'Current');
        return `${translatedFrom} - ${translatedTo}`;
    } else {
        let formattedTo = moment((to._attributes.year + "/" + to._attributes.month + "/01").replace("--", "")).format(`MMMM${largeSpace}YYYY`);
        let translatedTo = translateAndKeepFormat(formattedTo, language);
        return `${translatedFrom} - ${translatedTo}`;
    }
};
let getProjectDescription = (e) => {
    return e.ProjectDescription ? e.ProjectDescription._cdata : "";
}
let getResponsibilities = (e) => {
    return e.Responsibilities ? e.Responsibilities._cdata : "";
}
let getEnvironment = (e) => {
    return e.Environment ? e.Environment._cdata : "";
}

function computeLanguages(learnerInfo, lang) {
    let languages = learnerInfo.Skills.Linguistic.LanguageList.Description;
    let cloned = Object.assign([], languages)
    let copiedLanguages = JSON.parse(JSON.stringify(cloned))
    if (!Array.isArray(copiedLanguages)) {
        copiedLanguages = Array(copiedLanguages)
    }
    if (copiedLanguages.length > 0) {
        for (const language of copiedLanguages) {
            language.label = {
                "_text": language.code && language.code ? translation(lang, computeLanguageLabel(language.code._text)) : ""
            }
            language.level = {
                "_text": language.level && language.level ? translation(lang, language.level._text) : ""
            }
        }
    }
    return copiedLanguages;
}

function computeExperiences(learnerInfo, language) {
    let sortedExperiences = learnerInfo.WorkExperienceList.WorkExperience;
    let cloned = Object.assign([], sortedExperiences)
    let copiedExperiences = JSON.parse(JSON.stringify(cloned))
    if (!Array.isArray(copiedExperiences)) {
        copiedExperiences = Array(copiedExperiences)
    }
    sortExperiences(copiedExperiences)
    if (copiedExperiences.length > 0) {
        for (const e of copiedExperiences) {

            let hasProjectDescription = getProjectDescription(e) !== undefined && getProjectDescription(e) !== "<p><br></p>"
            let hasResponsibilities = getResponsibilities(e) !== undefined && getResponsibilities(e) !== "<p><br></p>"
            let hasEnvironment = getEnvironment(e) !== undefined && getEnvironment(e) !== "<p><br></p>"

            let projectDescription = hasProjectDescription ? computeTextToMultilevel(computeHtmlToText(getProjectDescription(e))) : ""
            let responsibilities = hasResponsibilities ? computeTextToMultilevel(computeHtmlToText(getResponsibilities(e))) : ""
            let environment = hasEnvironment ? computeTextToMultilevel(computeHtmlToText(getEnvironment(e))) : ""

            if (e) {
                e.title = getLabelByExperience(e)
                e.period = computePeriod(e, language)
                e.hasProjectDescription = hasProjectDescription
                e.projectDescription = projectDescription
                e.hasResponsibilities = hasResponsibilities
                e.responsabilities = responsibilities
                e.hasEnvironment = hasEnvironment
                e.environment = environment
            }
        }
    }
    return copiedExperiences;
}

function computeHtmlToText(htmlText) {
    let parser = new DOMParser;
    let decodedHtml = parser.parseFromString(htmlText, 'text/html').body.textContent;

    // removing all types of bullets
    return decodedHtml.replaceAll(/^\s*(?:[\dA-Z]+\.|[a-z]\))\s+/gm, "")
}

function computeListFromString(data) {
    let listFromString = data.split("\n");
    let cloned = Object.assign([], listFromString)
    let copied = JSON.parse(JSON.stringify(cloned))
    if (!Array.isArray(copied)) {
        copied = Array(copied)
    }
    let final = [];
    if (copied.length > 0) {
        for (const elt of copied) {
            if (elt !== "") {
                final.push({
                    label: elt
                })
            }

        }
    }
    return final;
}

const blobToUint8Array = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(new Uint8Array(reader.result));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(blob);
    });
};

export async function downloadGeneratedFile(token, siteId, fileName, docxFile, documentType, props, zipFile) {

    if (documentType === FileExtensionEnum.PDF) {
        await uploadFileToSharePoint(token, siteId, fileName.replace(".xml", "") + ".docx", docxFile)
        const convertResponse = await convertDocxFileToPdf(token, siteId, fileName.replace(".xml", "") + ".docx", documentType)
        const uint8Array = await blobToUint8Array(convertResponse)
        await deleteFileFromSharePoint(token, siteId, fileName.replace(".xml", "") + ".docx")
        if (zipFile === null) {
            saveAs(convertResponse, fileName.replace(".xml", "") + ".pdf", false);
            props.onLoading(false);
            props.notify("The Consultant Profile has been generated !", "success");
        } else {
            zipFile.file(fileName.replace(".xml", "") + ".pdf", uint8Array);
        }

    } else {
        const uint8Array = await blobToUint8Array(docxFile)
        if (zipFile === null) {
            saveAs(docxFile, fileName.replace(".xml", "") + ".docx", false);
            props.onLoading(false);
            props.notify("The Consultant Profile has been generated !", "success");
        } else {
            zipFile.file(fileName.replace(".xml", "") + ".docx", uint8Array);
        }

    }

}

export async function generateDocument(token, webUrl, siteId, fileName, language, cvDetails, props, documentType, zipFile) {
    props.onLoading(true);
    return await new Promise((resolve) => {
        loadFile(token, webUrl, siteId, language, documentType, async function (
                error,
                content
            ) {
                if (error) {
                    props.notify("An error has occurred !", "error");
                    throw error;
                }
                let zip = new PizZip(content);
                let doc = new Docxtemplater(zip, {parser: angularParser, linebreaks: true});

                const learnerInfo = cvDetails.SkillsPassport.LearnerInfo;
                let trainingText = learnerInfo.EducationAndTraining.Training.Description._text;
                let certifText = learnerInfo.EducationAndTraining.Certification.Description._text;
                let educationText = learnerInfo.EducationAndTraining.Education.Description._text;
                let technicalSkillsText = learnerInfo.Skills.TechnicalSkills.Description._text;
                let coreSkillsText = (learnerInfo.ProfileOverview.CoreSkills && learnerInfo.ProfileOverview.CoreSkills.Label._text !== undefined) ? learnerInfo.ProfileOverview.CoreSkills.Label._text : ""
                let educationNewFormatList = learnerInfo.EducationAndTraining.Education.List
                let trainingNewFormatList = learnerInfo.EducationAndTraining.Training.List
                let certificationNewFormatList = learnerInfo.EducationAndTraining.Certification.List

                let educationList = educationText !== undefined ? computeListFromString(computeHtmlToText(educationText)) : [];
                let trainingList = trainingText !== undefined ? computeListFromString(computeHtmlToText(trainingText)) : [];
                let certificationList = certifText !== undefined ? computeListFromString(computeHtmlToText(certifText)) : [];
                let technicalSkillsList = technicalSkillsText !== undefined ? computeListFromString(computeHtmlToText(technicalSkillsText)) : [];
                let coreSkillsList = coreSkillsText !== undefined ? computeListFromString(computeHtmlToText(coreSkillsText)) : [];
                let educationNewFormat = educationNewFormatList !== undefined ? generateTitles(educationNewFormatList) : [];
                let trainingNewFormat = trainingNewFormatList !== undefined ? generateTitles(trainingNewFormatList) : [];
                let certificationNewFormat = certificationNewFormatList !== undefined ? generateTitles(certificationNewFormatList) : [];
                let hasEducation = educationList.length > 0 || educationNewFormat?.length > 0
                let hasTraining = trainingList.length > 0 || trainingNewFormat?.length > 0
                let hasCertification = certificationList.length > 0 || certificationNewFormat?.length > 0
                let hasCoreSkills = coreSkillsList.length > 0
                let identification = learnerInfo.Identification;

                let profileOverview = learnerInfo.ProfileOverview.Description.Label._text !== undefined ? computeTextToMultilevel(computeHtmlToText(learnerInfo.ProfileOverview.Description.Label._text)) : "";
                let coreSkills = coreSkillsList;
                let technicalSkills = technicalSkillsList;
                let education = educationList;
                let training = trainingList;
                let certification = certificationList;
                let languages = computeLanguages(learnerInfo, language);
                let sortedExperiences = computeExperiences(learnerInfo, language);

                doc.setData({
                    identification: identification ? identification : "",
                    position: learnerInfo.Headline.Description.Label._text,
                    profileOverview: profileOverview,
                    coreSkills: coreSkills,
                    languages: languages,
                    technicalSkills: technicalSkills,
                    education: education,
                    training: training,
                    certification: certification,
                    educationNewFormat: educationNewFormat,
                    trainingNewFormat: trainingNewFormat,
                    certificationNewFormat: certificationNewFormat,
                    experiences: sortedExperiences,
                    hasCertification: hasCertification,
                    hasEducation: hasEducation,
                    hasTraining: hasTraining,
                    hasCoreSkills: hasCoreSkills
                });
                try {
                    doc.render();
                } catch (error) {
                    props.notify("An error has occurred !", "error");

                    // The error thrown here contains additional information when logged with JSON.stringify (it contains a properties object containing all suberrors).

                    if (error.properties && error.properties.errors instanceof Array) {
                        error.properties.errors
                            .map(function (error) {
                                return error.properties.explanation;
                            })
                            .join("\n");
                    }
                    throw error;
                }
                var out = doc.getZip().generate({
                    type: "blob",
                    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                }); //Output the document using Data-URI
                resolve(await downloadGeneratedFile(token, siteId, fileName, out, documentType, props, zipFile))
            }
        );
    })
}

